import { Component, OnInit, ViewEncapsulation, Input, Output, EventEmitter, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { first } from 'rxjs/operators';

import { QuizService, AutoSubOnDestroy, AutoSub, MoteEventService, StatsService, UserMoteService } from '../../providers';
import { QuizQuestion, Challenge, QuizAnswer, MoteEvent, Mote, MoteEventType, ChallengeState, StatType, QuizAnswerMethod, MoteType, Product, Voucher } from '../../models';
import { MotesLoginService } from '@motes-sdk/motes-login';

enum QuizState {
    INIT = "INIT",
    CLOSED = "CLOSED",
    TAP_TO_QUIZ = "TAP_TO_QUIZ",
    SHOW_COUNT_DOWN = "SHOW_COUNT_DOWN",
    SHOW_QUESTION = "SHOW_QUESTION",
    SHOW_ANSWER = "SHOW_ANSWER",
    ALREADY_ANSWERED = "ALREADY_ANSWERED"
}

@AutoSubOnDestroy()
@Component({
    selector: 'quiz-popup',
    templateUrl: './quiz-popup.component.html',
    styleUrls: ['./quiz-popup.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class QuizPopupComponent implements OnInit, OnDestroy {
    @Input() enableSimulation: boolean = false;
    @Output() openChallenges = new EventEmitter<[Challenge, Mote]>();

    public quizState: QuizState = QuizState.INIT;
    public challenge: Challenge = null;
    public selectedAnswer: QuizAnswer;
    public penaltyCountdown: number = 0;
    public penaltyCounter = null;
    public question: QuizQuestion = null;
    public correctAnswerList: string[] = [];
    public isLastQuest: boolean = false;
    public soundInitialized: boolean = false;
    public pauseBonusCountDown: boolean = false;
    public tourIsQuest: boolean = false;

    // List with answers from question.answers in randomized order
    public answerList: QuizAnswer[] = [];
    private stateTimeout = null;

    private initSubject: ReplaySubject<void> = new ReplaySubject<void>();
    private stateInitializedSubject: ReplaySubject<void> = new ReplaySubject<void>();

    public challengeState: ChallengeState = null;

    public bonusPointScore: number = 0;
    public mote: Mote = null;

    public isLoggedIn = false;

    // Export to view 
    public QuizState = QuizState;
    public QuizAnswerMethod = QuizAnswerMethod;

    constructor(public quizService: QuizService,
        private moteEventService: MoteEventService,
        private userMoteService: UserMoteService,
        private statsService: StatsService,
        public loginService: MotesLoginService,
        private cd: ChangeDetectorRef) {
    }

    ngOnInit() {
        // Listen for user rights
        AutoSub(this).reg['userRightsSub'] = this.loginService.watchUserRights().subscribe(userRights => {
            this.isLoggedIn = (userRights && !userRights.isAnonymous);
        });

        // Listen for challengeStateMap updates for either user or group member (determined by the presence of a group key on the user)
        // TODO: Implement another system where quiz popup state is not controlled by user challenge state to avoid quiz popup from
        // opening on group change and user change in all cases (currently only partly prevented)
        AutoSub(this).reg['challengeState'] = this.quizService.watchUserChallengeStateMap().subscribe(async (challengeStateMap) => {
            await this.awaitInit();
            this.challengeState = challengeStateMap.getChallengeState(this.challenge.key);

            // Do nothing if no mote quiz is loaded
            if(!this.mote) return;
            // Notify listeners (hide/show quiz button and determine if popup button should be shown to the user)
            this.emitQuizQuestionComplete();

            if(!this.hasQuizStarted()) this.initChallengeQuestionState(this.challengeState);
        });

        // Initiate load of quiz (update quiz question state and load challenge)
        AutoSub(this).reg['moteQuizLoadEvent'] = this.moteEventService.watchEventType(MoteEventType.QUIZ_PRELOAD).subscribe((ev) => {
            // Ignore preload event with no mote if the user is currently quizzing (only close quiz-popup in case user is not interacting with quiz)
            if(this.hasQuizStarted() && !ev.mote) return;
            // Preload the given mote (or close popup in case no mote was given in event)
            this.setMote(ev.mote);
        });

        // Invoke `startQuiz()` on received `QUIZ_STARTED` events
        AutoSub(this).reg['moteQuizStartEvent'] = this.moteEventService.watchEventType(MoteEventType.QUIZ_STARTED).subscribe(async (ev) => {
            // Load mote quiz question and challenge (if not already loaded)
            await this.setMote(ev.mote);
            // Open the quiz popup
            this.openQuiz(true);
        });

        // Close group quiz if canceled by group confirm popup.. "Are you sure?"
        AutoSub(this).reg['moteQuizQuitEvent'] = this.moteEventService.watchEventType(MoteEventType.QUIZ_SKIPPED).subscribe(async (ev) => {
            this.setState(QuizState.CLOSED);
        });
    }

    /**
     * Set the active quiz mote and load challenge
     */
    private async setMote(mote: Mote) {
        // Check if we already loaded the quiz question of this mote
        if(mote?.key == this.mote?.key) return;

        this.mote = mote;
        this.question = mote ? mote.quiz : null;
        // console.log("Mote question ", this.question);

        if(!this.question || this.question.disabled) {
            this.emitQuizAvailable(false);
            return;
        }

        if(!this.question.challenge) {
            console.warn("No challenge in question??", this.question);
            this.emitQuizAvailable(false);
            this.setState(QuizState.CLOSED);
            return;
        }

        // Order answers randomly 
        this.answerList = this.question.answers.slice().sort((a, b) => 0.5 - Math.random());
        // Extract the correct answer labels
        this.correctAnswerList = this.question.answers.filter((answer) => answer.isCorrect).map((answer) => answer.label);

        let challenge = await this.quizService.getChallenge(this.question.challenge);
        this.challenge = challenge;
        // Figure out if this is a quest, and if this is the last quest question
        await this.detectQuest(mote);

        // Init max bonus points on challenge change event
        this.bonusPointScore = challenge.maxBonusPoints;
        this.emitQuizAvailable(challenge ? this.isPublished() : false);

        this.initSubject.next();
        if(challenge && (challenge.published || this.enableSimulation)) this.openQuiz();
    }

    private async detectQuest(mote: Mote) {
        let parent = await this.userMoteService.getUserMote(mote.parent);
        if(!parent) {
            this.tourIsQuest = false;
            this.isLastQuest = false;
        }
        // Check if parent is a quest
        this.tourIsQuest = parent.type == MoteType.QUEST;
        // Try to get next mote
        let nextMote = await this.userMoteService.getNextMote(this.mote);
        // If next mote exist this is not the last mote in quest
        this.isLastQuest = (!nextMote) ? true : false;
    }

    /**
     * Check whether the current quiz question has been started.
     * Hence, the method will also return `true` for completed quiz questions.
     * @returns `true` if the quiz question has been started. Otherwise `false`.
     */
    private hasQuizStarted(): boolean {
        return !(this.quizState == QuizState.INIT || this.quizState == QuizState.CLOSED || this.quizState == QuizState.TAP_TO_QUIZ);
    }

    private showLoginPopup(): boolean {
        if(!this.challenge.requireUserLogin || this.isLoggedIn) return false;
        // Show login popup
        this.loginService.requestUserLogin($localize`Please sign in or create an account to participate in the quiz. By signing up you agree, that your email may be used to contact you in case you win a price.`);
        return true;
    }

    /**
     * Open the quiz popup and let the quiz begin
     */
    public async openQuiz(forceOpen: boolean = false) {
        if(forceOpen && this.showLoginPopup()) return;

        await this.awaitChallengeInit();
        const completed = this.emitQuizQuestionComplete();

        // Enforce count down penalty on reopen - no cheating
        // Out comment this as penalty is no longer enabled - consider deleting penaltyCountdown completely as design update has broken it
        // if(this.penaltyCountdown > 0) {
        //     console.log("Penalty countdown is bigger than 0")
        //     this.setState(QuizState.SHOW_ANSWER);
        //     return;
        // }

        // Do nothing if quiz already answered (requestQuiz will open challenges when pressing on mascot)
        if(this.quizState == QuizState.ALREADY_ANSWERED) {
            return;
        }

        // Figure if count down should be shown before question
        let showQuizEv = this.challenge.countDownEnabled ? QuizState.SHOW_COUNT_DOWN : QuizState.SHOW_QUESTION
        // Force open of quiz or first show tap to quiz
        let openEvent = forceOpen ? showQuizEv : QuizState.TAP_TO_QUIZ;
        this.setState(completed ? QuizState.ALREADY_ANSWERED : openEvent);
    }

    private awaitInit(): Promise<void> {
        return this.initSubject.pipe(first()).toPromise();
    }

    private emitQuizAvailable(isAvailable: boolean) {
        this.quizService.emitQuizQuestionAvailable(this.mote, isAvailable);
    }

    private async awaitChallengeInit() {
        await this.awaitInit();
        await this.stateInitializedSubject.pipe(first()).toPromise();
    }

    private emitQuizQuestionComplete(): boolean {
        const completed = this.tourIsQuest ? (this.challengeState.correctAnswersMap[this.mote.key] || false) : (this.mote.key in this.challengeState.correctAnswersMap);
        this.quizService.emitQuizQuestionComplete(this.mote, completed);
        return completed;
    }

    private async initChallengeQuestionState(challengeState: ChallengeState) {
        if(!this.challenge) return console.warn("No challenge in quiz popup, yet??");
        if(!this.mote) return console.warn("No mote input, can not check challenge state.");
        // Get point of this challenge question
        let isAnswered = challengeState.correctAnswersMap[this.mote.key];
        // if(pointsGiven > 0) console.warn("Points already given ", pointsGiven);
        this.stateInitializedSubject.next();

        // Don't open popup if this challenge is not published yet
        if(!this.isPublished()) return this.setState(QuizState.CLOSED);

        // Show quiz or show already answered if user already got points here
        this.setState(isAnswered ? QuizState.ALREADY_ANSWERED : QuizState.TAP_TO_QUIZ);
    }

    private isPublished(): boolean {
        return (this.challenge.published || this.enableSimulation);
    }

    public setState(state: QuizState) {
        // console.log("Set quiz state ", state);

        // Clear any pending timeouts
        if(this.stateTimeout) this.clearStateTimer();
        // Delay any initial event (to show CSS animation)
        //if(state == QuizState.ALREADY_ANSWERED) return this.setStateDelayed(QuizState.CLOSED, 2500);
        if(this.quizState == QuizState.INIT) return this.setStateDelayed(state, 600);
        // console.log("Set quiz state: ", state);        
        this.quizState = state;

        // Indicate to parent (app) to hide top bar
        this.quizService.quizPopupShown = (state == QuizState.SHOW_COUNT_DOWN || state == QuizState.SHOW_ANSWER || state == QuizState.SHOW_QUESTION);

        if(state == QuizState.SHOW_QUESTION || state == QuizState.SHOW_ANSWER) this.disableBodyScroll();
        else this.enableBodyScroll();

        if(state == QuizState.SHOW_QUESTION) this.selectedAnswer = null;

        if(state == QuizState.ALREADY_ANSWERED) this.setStateDelayed(QuizState.CLOSED, 2500);
    }

    private disableBodyScroll() {
        if(typeof document == 'undefined' || !document.body.style) return;
        document.body.style.overflow = 'hidden';
    }

    private enableBodyScroll() {
        if(typeof document == 'undefined' || !document.body.style) return;
        document.body.style.overflow = 'auto';
    }

    private clearStateTimer() {
        if(!this.stateTimeout) return;
        clearTimeout(this.stateTimeout);
        this.stateTimeout = null;
    }

    private setStateDelayed(state: QuizState, delay: number) {
        // Clear any other pending set states
        this.clearStateTimer();
        // Change state to closed after showing already answered
        this.stateTimeout = setTimeout(() => {
            // console.log("Set state delayed: ", state);
            this.quizState = state;
            // Ensure that ALREADY_ANSWERED close is applied
            this.setState(state);
        }, delay);
    }

    public async doAnswer(answer: QuizAnswer, quizContent: HTMLElement) {
        // Update answer's title and info
        this.selectedAnswer = answer;
        // If tour is not a quest, answer is incorrect or incomplete, fill in title
        if(!this.tourIsQuest && (!answer.isCorrect || !answer.title)) answer.title = this.getRandomAnswerTitle(answer.isCorrect);

        // Emit quiz points to service
        this.emitQuizPoints(answer.isCorrect, this.bonusPointScore);

        // Register answer in stats
        let statType = answer.isCorrect ? StatType.QUIZ_ANSWER_RIGHT : StatType.QUIZ_ANSWER_WRONG;
        this.statsService.addStatsEvent(statType, this.mote);

        // Stop bonus timer
        this.pauseBonusCountDown = true;

        // And wait a bit to increase suspense highlighting the answer
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 3000));
        this.cd.markForCheck();

        // Start the timer again
        this.pauseBonusCountDown = false;

        // Start penalty count down on 
        if(!answer.isCorrect) {
            this.startPenalty();
        }

        this.setState(QuizState.SHOW_ANSWER);
        quizContent.scroll({ top: 0 });
    }

    private getRandomAnswerTitle(isAnswerCorrect: boolean) {
        const lib = isAnswerCorrect
            ? [$localize`Weehoo!`, $localize`Yeah!`, $localize`Correct!`, $localize`Yeehaw!`, $localize`Well done!`]
            : [$localize`Oh no!`, $localize`Too bad`, $localize`Frack!`, $localize`Shoot!`, $localize`Oh, snap!`];
        const randomIdx = Math.floor(Math.random() * lib.length);
        return lib[randomIdx];
    }

    public doAnswerFreeText(textInput: HTMLInputElement, quizContent: HTMLElement) {
        // Get text answer from input
        let textAnswer = textInput.value.toLowerCase().trim();
        // Try to find the answer
        let answer = this.question.answers.find(answer => (answer.label.toLowerCase() == textAnswer));
        this.selectedAnswer = answer;

        // Use first incorrect answer response if answer was not found
        if(!answer) answer = this.question.answers.find(answer => !answer.isCorrect);
        // Generate a invalid answer if non was created for this question
        if(!answer) answer = this.getDefaultWrongAnswer();

        // Handle answer like multiple choice answer
        this.doAnswer(answer, quizContent);
    }

    private getDefaultWrongAnswer(): QuizAnswer {
        let answer = new QuizAnswer();
        answer.isCorrect = false;
        answer.label = $localize`Wrong answer`;
        answer.title = $localize`Nooooo!`;
        answer.info = $localize`Unfortunately your answer is incorrect.`;
        return answer;
    }

    private startPenalty() {
        this.clearPenaltyCounter();

        if(this.challenge.timePenalty <= 0) return;
        // Reset counter
        this.penaltyCountdown = this.challenge.timePenalty;
        // Start count down every this.penaltyCountdown
        this.penaltyCounter = setInterval(() => {
            // Count down
            this.penaltyCountdown = this.penaltyCountdown - 1;
            // Clear interval when done counting
            if(this.penaltyCountdown <= 0) {
                this.clearPenaltyCounter();
            }
            this.cd.markForCheck();
        }, 1000);
    }

    private clearPenaltyCounter() {
        if(!this.penaltyCounter) return;

        clearInterval(this.penaltyCounter);
        this.penaltyCounter = null;
        this.penaltyCountdown = 0;
    }

    public closeQuiz(ev: UIEvent) {
        ev.stopImmediatePropagation();
        // During group play, display confirmation dialog before skipping question (because the action results in 0 points being awarded)
        if(this.quizService.isGroupQuiz()) {
            this.emitQuizSkipRequest();
            return;
        }

        // Make suer user cant cheat and close quiz with bonus
        this.bonusPointScore = 0;
        this.setState(QuizState.CLOSED);
    }

    public countDownDone() {
        // Show question after count down
        this.setState(QuizState.SHOW_QUESTION);
    }

    public showQuestion(ev: UIEvent) {
        ev.stopImmediatePropagation();
        this.setState(QuizState.SHOW_QUESTION);
    }

    public quizSuccess(ev: UIEvent) {
        ev.stopImmediatePropagation();
        this.emitQuestionCompleted();
        this.setState(QuizState.CLOSED);
    }

    public voucherClaimed(voucher: Voucher) {
        if(voucher) {
            this.moteEventService.emitMoteEvent({ mote: this.mote, type: MoteEventType.VOUCHER_CLAIMED, options: { voucherKey: voucher.key, productKey: voucher.productKey } });
        }
        this.setState(QuizState.CLOSED);
    }

    private emitQuizSkipRequest() {
        const event = { type: MoteEventType.QUIZ_SKIP_REQUESTED, mote: this.mote };
        this.moteEventService.emitMoteEvent(event);
    }

    private emitQuestionCompleted() {
        let moteEv: MoteEvent = { type: MoteEventType.QUIZ_COMPLETED, mote: this.mote }
        // Emit completed event
        this.moteEventService.emitMoteEvent(moteEv);
    }

    private emitQuizPoints(isCorrect: boolean, bonusPoints: number): void {
        // Get negative / positive points from challenge
        let points = (isCorrect) ? this.challenge.correctAnswerPoints : this.challenge.wrongAnswerPoints;

        let moteEv: MoteEvent = {
            type: MoteEventType.QUIZ_POINT,
            mote: this.mote,
            options: {
                points: points,
                bonusPoints: (isCorrect) ? bonusPoints : 0,
                isCorrectAnswer: isCorrect,
                challengeKey: this.challenge.key
            }
        }
        // Emit point event
        this.moteEventService.emitMoteEvent(moteEv);
    }

    /**
     * Request quiz start (from view)
     */
    public requestQuiz(mote: Mote, quizState: QuizState) {
        // Ensure that group play does not show ready for quiz state if question already answered 
        if(quizState == QuizState.ALREADY_ANSWERED) {
            // Open challenge page to show current challenge state
            this.openChallenges.next([this.challenge, this.mote]);
            return;
        }
        // Send QUIZ_REQUESTED request for the quiz to start
        this.quizService.requestQuiz(mote);
    }

    ngOnDestroy() {
        this.enableBodyScroll();
    }
}
